/**
 * Cabinet session: persisted Zyphr tokens + a tiny event bus so the
 * footer UI (signed-in/signed-out state) reacts to changes without
 * page reloads.
 *
 * Storage: localStorage under arcadia84:auth:session-v1. The schema is
 * versioned so a breaking change ships a v2 key, ignoring the v1 row
 * (player gets logged out — acceptable).
 *
 * Refresh strategy: getAccessToken() returns the cached token if it has
 * more than REFRESH_WINDOW_MS until expiry; otherwise it triggers a
 * refresh via the Zyphr SDK and returns the new token. Concurrent calls
 * during a refresh share the same in-flight Promise — no duplicate
 * network calls.
 */

import { refreshSession } from './api-client.js';

const STORAGE_KEY = 'arcadia84:auth:session-v1';
const REFRESH_WINDOW_MS = 5 * 60 * 1000;

export interface Session {
  /** Bearer token sent on Authorization. */
  accessToken: string;
  /** Used to mint new access tokens. */
  refreshToken: string;
  /** Wall-clock ms when accessToken expires. */
  expiresAt: number;
  /** Player's email. Shown in the footer + used for support reqs. */
  email: string;
  /** Zyphr user UUID. Same value the server sees on introspection. */
  userId: string;
}

type Listener = (session: Session | null) => void;

const listeners = new Set<Listener>();
let inFlightRefresh: Promise<Session> | null = null;

function readStorage(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      !parsed.accessToken ||
      !parsed.refreshToken ||
      typeof parsed.expiresAt !== 'number' ||
      !parsed.email ||
      !parsed.userId
    ) {
      return null;
    }
    return parsed as Session;
  } catch {
    return null;
  }
}

function writeStorage(session: Session | null): void {
  try {
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* private mode or quota — degraded but functional */
  }
}

function notify(session: Session | null): void {
  for (const fn of listeners) {
    try {
      fn(session);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[auth] session listener threw', err);
    }
  }
}

export function getSession(): Session | null {
  return readStorage();
}

export function setSession(session: Session): void {
  writeStorage(session);
  notify(session);
}

export function clearSession(): void {
  writeStorage(null);
  notify(null);
}

export function isSignedIn(): boolean {
  return getSession() !== null;
}

/** Subscribe to session changes. Returns an unsubscribe fn. */
export function onSessionChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Returns a valid access token, refreshing if within the expiry
 * window. Returns null if no session exists. Throws if the refresh
 * call fails — caller decides whether that means "log out the user"
 * or "retry later" (most callers should fall back to anonymous).
 */
export async function getAccessToken(): Promise<string | null> {
  const session = getSession();
  if (!session) return null;
  if (session.expiresAt - Date.now() > REFRESH_WINDOW_MS) {
    return session.accessToken;
  }
  // Within refresh window — try to refresh. Coalesce concurrent calls.
  if (!inFlightRefresh) {
    inFlightRefresh = refreshNow(session).finally(() => {
      inFlightRefresh = null;
    });
  }
  try {
    const fresh = await inFlightRefresh;
    return fresh.accessToken;
  } catch (err) {
    // Refresh failed. Most likely the refresh token expired or was
    // revoked. Clear the session so the player is treated as anonymous
    // until they sign in again.
    // eslint-disable-next-line no-console
    console.warn('[auth] refresh failed, clearing session', err);
    clearSession();
    return null;
  }
}

async function refreshNow(session: Session): Promise<Session> {
  // API-side proxy already strips Zyphr's envelope and normalizes
  // the response shape — see apps/api/src/routes/v1/auth.routes.ts.
  const resp = await refreshSession(session.refreshToken);
  const fresh: Session = {
    accessToken: resp.accessToken,
    refreshToken: resp.refreshToken,
    expiresAt: Date.now() + resp.expiresIn * 1000,
    // The API returns the latest email/userId from Zyphr; prefer those
    // over the cached values in case the player updated their profile.
    email: resp.email,
    userId: resp.userId,
  };
  setSession(fresh);
  return fresh;
}
